import React, { useState, useRef } from "react";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ImportStatementModal = ({ isOpen, onClose, onSuccess, bankAccounts = [] }) => {
  const [bankAccountId, setBankAccountId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    if (isOpen) {
      const first = bankAccounts.filter(b => b.is_active !== false)[0];
      setBankAccountId(first?.id || "");
      setFile(null);
    }
  }, [isOpen, bankAccounts]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecione um arquivo OFX ou CSV."); return; }
    if (!bankAccountId) { toast.error("Selecione a conta de destino."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bank_account_id", bankAccountId);
      const r = await axios.post(`${API}/finance/import-statement`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported, parser, bank_account } = r.data;
      toast.success(`${imported} lançamento(s) importado(s) de ${parser.toUpperCase()} para ${bank_account}. Vá em Pendentes para classificar.`);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao importar extrato.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-md bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden" data-testid="modal-import-statement">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <Upload className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">Importar Extrato Bancário</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300">

          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-[11px] text-slate-300">
            <p className="font-semibold text-amber-300 mb-1">📥 Como funciona?</p>
            <p>Envie um arquivo <b>OFX</b> ou <b>CSV</b> do seu banco. Cada linha vira um lançamento <b>Pendente</b> vinculado à conta selecionada. Depois é só classificar em lote.</p>
            <p className="mt-1 text-slate-400 italic">CSV esperado: <code>Data;Descrição;Valor</code> (valores negativos = despesas).</p>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Conta Bancária de Destino *</label>
            <select
              data-testid="select-import-bank"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              required
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            >
              <option value="">— Escolha uma conta —</option>
              {bankAccounts.filter(b => b.is_active !== false).map((ba) => (
                <option key={ba.id} value={ba.id}>{ba.name}{ba.bank_name ? ` (${ba.bank_name})` : ""}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Arquivo do Extrato *</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-full bg-[#080C14] border-2 border-dashed border-slate-600 rounded-xl p-6 text-center cursor-pointer hover:border-[#D4AF37] transition"
              data-testid="dropzone-import-statement"
            >
              {file ? (
                <>
                  <FileText className="w-8 h-8 mx-auto text-[#D4AF37] mb-2" />
                  <p className="text-sm text-white font-semibold">{file.name}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB • clique para trocar</p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-slate-500 mb-2" />
                  <p className="text-slate-400">Clique para escolher o arquivo</p>
                  <p className="text-[10px] text-slate-600 mt-1">Formatos: .ofx, .csv</p>
                </>
              )}
              <input
                ref={fileInputRef}
                data-testid="input-import-file"
                type="file"
                accept=".ofx,.csv,.txt"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs">Cancelar</button>
            <button
              data-testid="btn-submit-import"
              type="submit"
              disabled={uploading}
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)] disabled:opacity-50"
            >
              {uploading ? "Importando..." : "Importar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
